import { createSlice, createAsyncThunk, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { normalize } from "normalizr";
import { api as authApi } from "../../../services/auth/api";
import User from "../../../services/user";
import { Schemas } from "../../../shared/redux/schemas";
import { notify } from "../../../shared/redux/actions/notifications";
import Errors from "../../../utils/errors";
import { RootState } from "../../../shared/redux/types";
import { getUser } from "../../../shared/redux/selectors/users";
import { ArnoldBaseQueryError } from "../../../services/baseQuery";

export const authenticate = createAsyncThunk("auth/authenticate", async () => {
    const user = await User.current();
    return normalize(user, Schemas.USER);
});

export const logout = createAsyncThunk("auth/logout", async (_, { dispatch }) => {
    try {
        await dispatch(authApi.endpoints.logout.initiate()).unwrap();
        dispatch(notify({ translatedText: "messages.auth.logged_out", autoClear: true }));
        return null;
    } catch (error) {
        const queryError = error as ArnoldBaseQueryError;
        const errorsParser = new Errors(queryError?.data?.errors || []);
        errorsParser.errors.forEach((err: { code: string }) => {
            dispatch(notify({ translatedText: `errors.messages.${err.code}` }));
        });
        throw error;
    }
});

export const LOGOUT_REQUEST = logout.pending.type;
export const LOGOUT_SUCCESS = logout.fulfilled.type;

export interface AuthState {
    user: number | null;
    isAuthenticating: boolean;
    isAuthenticated: boolean;
    authError: SerializedError | null;
}

const initialState: AuthState = {
    user: null,
    isAuthenticating: true,
    isAuthenticated: false,
    authError: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(authenticate.pending, (draft) => {
                draft.isAuthenticating = true;
                draft.authError = null;
            })
            .addCase(
                authenticate.fulfilled,
                (draft, { payload }: PayloadAction<{ result: number }>) => {
                    draft.user = payload.result;
                    draft.isAuthenticated = true;
                    draft.isAuthenticating = false;
                },
            )
            .addCase(authenticate.rejected, (draft, { error }) => {
                draft.isAuthenticated = false;
                draft.isAuthenticating = false;
                draft.authError = error;
            })
            .addCase(logout.fulfilled, (draft) => {
                draft.isAuthenticated = false;
                draft.isAuthenticating = false;
                draft.user = null;
            });
    },
});

export const authenticatedUserId = (state: RootState) => state.auth.user;
export const authenticatedUser = (state: RootState) => getUser(state, authenticatedUserId(state));
export const isAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const isAuthenticating = (state: RootState) => state.auth.isAuthenticating;

export default authSlice.reducer;
